import { Center, Loader, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconBellCheck } from '@tabler/icons-react';
import { useNotifications } from 'src/notifications/hooks/useNotifications';
import {
  activeNotifications,
  filterNotifications,
  type NotificationFilter,
} from 'src/notifications/notificationFilters';
import { notificationGroupLabel } from 'src/notifications/notificationCards';
import type { Notification, NotificationType } from 'src/notifications/types/Notification';
import NotificationCard from './NotificationCard';

function groupByType(notifications: Notification[]): [NotificationType, Notification[]][] {
  const groups = new Map<NotificationType, Notification[]>();
  for (const notification of notifications) {
    const existing = groups.get(notification.payload.type) ?? [];
    existing.push(notification);
    groups.set(notification.payload.type, existing);
  }
  return [...groups.entries()];
}

export default function NotificationList({
  onClose,
  filter,
  advisorOf,
}: {
  onClose: () => void;
  filter: NotificationFilter;
  advisorOf: (notification: Notification) => string | null;
}) {
  const { data, isLoading } = useNotifications();

  if (isLoading) {
    return (
      <Center mih={160}>
        <Loader size="sm" />
      </Center>
    );
  }

  const notifications = data ?? [];
  const hasActive = activeNotifications(notifications).length > 0;
  const items = filterNotifications(notifications, filter, advisorOf);

  if (!hasActive) {
    return (
      <Center mih={200}>
        <Stack align="center" gap="xs">
          <ThemeIcon variant="light" color="gray" radius="xl" size={48}>
            <IconBellCheck size={24} />
          </ThemeIcon>
          <Text fw={600} c="gray.8">
            You are all caught up
          </Text>
          <Text size="sm" c="gray.6" ta="center">
            New notifications will appear here.
          </Text>
        </Stack>
      </Center>
    );
  }

  if (items.length === 0) {
    return (
      <Center mih={160}>
        <Text size="sm" c="gray.6" ta="center">
          No notifications match this filter.
        </Text>
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      {groupByType(items).map(([type, group]) => (
        <Stack key={type} gap="xs">
          <Text size="xs" tt="uppercase" c="gray.6" fw={700} style={{ letterSpacing: '0.04em' }}>
            {notificationGroupLabel(type)} &middot; {group.length}
          </Text>
          {group.map(notification => (
            <NotificationCard key={notification.id} notification={notification} onClose={onClose} />
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
